import type { TOptions, TPlayer } from 'video.js'

import { createLogger } from '|>/shared/logger'

const logger = createLogger('addons/wake-lock')

const PREACQUIRE_ADOPTION_TIMEOUT_MS = 10_000

const isWakeLockSupported = (): boolean =>
  typeof navigator !== 'undefined' && 'wakeLock' in navigator

let preacquiredSentinel: WakeLockSentinel | null = null

/**
 * Request wake lock during a user gesture before SPA navigation to the player.
 * The sentinel is adopted by inject() when the player initializes.
 */
export function preacquire(): void {
  if (!isWakeLockSupported()) return
  if (preacquiredSentinel) return

  navigator.wakeLock
    .request('screen')
    .then((sentinel) => {
      preacquiredSentinel = sentinel
      sentinel.addEventListener('release', () => {
        if (preacquiredSentinel === sentinel) preacquiredSentinel = null
      })

      // Release if inject() does not adopt (e.g. payment popup blocked navigation)
      setTimeout(() => {
        if (preacquiredSentinel === sentinel) {
          sentinel.release().catch(() => {})
          preacquiredSentinel = null
        }
      }, PREACQUIRE_ADOPTION_TIMEOUT_MS)
    })
    .catch(() => {})
}

/**
 * Configure wake lock addon before player initialization
 */
export function configure(options: TOptions): TOptions {
  return options
}

/**
 * Injects Screen Wake Lock to keep the device awake while content is playing.
 * Uses the native Wake Lock API (iOS 16.4+). No-op when unsupported.
 */
export function inject(player: TPlayer): TPlayer {
  if (!isWakeLockSupported()) {
    logger.debug('pureya/wake-lock unsupported')
    return player
  }

  let sentinel: WakeLockSentinel | null = null
  let acquiring = false
  /** When pause/ended arrives while request() is still pending */
  let releaseRequested = false

  const bindSentinelRelease = (lock: WakeLockSentinel) => {
    lock.addEventListener('release', () => {
      logger.debug('pureya/wake-lock released by system')
      if (sentinel === lock) sentinel = null
    })
  }

  if (preacquiredSentinel && !preacquiredSentinel.released) {
    sentinel = preacquiredSentinel
    preacquiredSentinel = null
    bindSentinelRelease(sentinel)
    logger.debug('pureya/wake-lock adopted preacquired')
  }

  // General acquire path (playing, visibilitychange). On iOS this often fails without
  // a user gesture — see onUserGesture below for the WebKit-specific duplicate path.
  const acquireWakeLock = async (): Promise<void> => {
    if (sentinel || acquiring || player.isDisposed?.()) return
    if (
      typeof document !== 'undefined' &&
      document.visibilityState !== 'visible'
    ) {
      return
    }

    releaseRequested = false
    acquiring = true

    try {
      sentinel = await navigator.wakeLock.request('screen')
      logger.debug('pureya/wake-lock acquired')
      bindSentinelRelease(sentinel)

      // pause/ended arrived while request() was in flight
      if (releaseRequested) {
        await releaseWakeLock()
      }
    } catch (err) {
      logger.debug('pureya/wake-lock acquire failed', { err })
      sentinel = null
    } finally {
      acquiring = false
    }
  }

  const releaseWakeLock = async (): Promise<void> => {
    releaseRequested = true

    if (!sentinel) return

    try {
      await sentinel.release()
      logger.debug('pureya/wake-lock released')
    } catch (err) {
      logger.debug('pureya/wake-lock release failed', { err })
    } finally {
      sentinel = null
    }
  }

  const onPlaying = () => {
    void acquireWakeLock()
  }

  const onRelease = () => {
    void releaseWakeLock()
  }

  const onVisibilityChange = () => {
    if (document.visibilityState !== 'visible') return
    if (player.isDisposed?.()) return
    if (player.paused()) return
    void acquireWakeLock()
  }

  // iOS/WebKit: request() must start in the same sync turn as touch/click (see acquireWakeLock).
  // Logic is duplicated on purpose — wrapping request() in async acquireWakeLock loses activation.
  const playerEl = player.el() as HTMLElement | null
  const gestureOpts: AddEventListenerOptions = { capture: true }

  const onUserGesture = (event: Event) => {
    // Do not gate on player.paused(): on resume the gesture fires while still paused.
    // Async acquire from `playing` loses iOS user activation — request here instead.
    if (sentinel || acquiring || player.isDisposed?.()) return
    if (document.visibilityState !== 'visible') return

    releaseRequested = false
    acquiring = true

    // Invoke request() in the same synchronous turn as the user gesture
    navigator.wakeLock
      .request('screen')
      .then((lock) => {
        logger.debug('pureya/wake-lock acquired (gesture)', {
          eventType: event.type,
        })

        sentinel = lock
        bindSentinelRelease(lock)

        if (releaseRequested) {
          void releaseWakeLock()
          return
        }

        // Gesture while paused: release if playback does not start within 5s
        if (player.paused()) {
          const tid = setTimeout(() => {
            if (player.paused() && sentinel === lock) void releaseWakeLock()
          }, 5000)
          const cancel = () => {
            clearTimeout(tid)
            player.off('playing', cancel)
          }
          player.on('playing', cancel)
        }
      })
      .catch((err: unknown) => {
        logger.debug('pureya/wake-lock acquire failed (gesture)', { err })
        sentinel = null
      })
      .finally(() => {
        acquiring = false
      })
  }

  player.on('playing', onPlaying)
  player.on('pause', onRelease)
  player.on('ended', onRelease)
  player.on('error', onRelease)
  player.on('playerreset', onRelease)

  document.addEventListener('visibilitychange', onVisibilityChange)
  playerEl?.addEventListener('touchstart', onUserGesture, gestureOpts)
  playerEl?.addEventListener('pointerdown', onUserGesture, gestureOpts)
  playerEl?.addEventListener('click', onUserGesture, gestureOpts)

  player.on('dispose', () => {
    player.off('playing', onPlaying)
    player.off('pause', onRelease)
    player.off('ended', onRelease)
    player.off('error', onRelease)
    player.off('playerreset', onRelease)
    document.removeEventListener('visibilitychange', onVisibilityChange)
    playerEl?.removeEventListener('touchstart', onUserGesture, gestureOpts)
    playerEl?.removeEventListener('pointerdown', onUserGesture, gestureOpts)
    playerEl?.removeEventListener('click', onUserGesture, gestureOpts)
    void releaseWakeLock()
  })

  return player
}
